import { getFontString, getVerticalOffset } from '../common/index';
import { getLineWidth } from './textMeasurements';
import { getWrappedTextLines, parseTokens } from './textWrapping';
import type { DrawingTextRun, ExcalidrawTextElement } from './types';

export type TextLayoutRun = Required<DrawingTextRun> & {
  x: number;
  width: number;
};
export type TextLayoutLine = {
  /** Source offsets, excluding synthetic soft line breaks. */
  start: number;
  end: number;
  text: string;
  width: number;
  height: number;
  baseline: number;
  runs: TextLayoutRun[];
  marker?: TextLayoutRun;
};

export const getTextRunContent = (runs: readonly DrawingTextRun[]) =>
  runs.map(run => run.text).join('');

type LayoutText = Pick<
  ExcalidrawTextElement,
  | 'fontSize'
  | 'fontFamily'
  | 'fontFace'
  | 'fontWeight'
  | 'fontStyle'
  | 'textDecoration'
  | 'strokeColor'
  | 'letterSpacing'
  | 'fontKerning'
  | 'lineHeight'
  | 'originalText'
  | 'textRuns'
  | 'paragraphSpacingBefore'
  | 'paragraphSpacingAfter'
  | 'listStyle'
  | 'textPadding'
>;

/** One geometry pipeline for editing, canvas painting and vector export. */
const getInlineTextLayout = (element: LayoutText, maxWidth?: number) => {
  const boundedWidth =
    maxWidth !== undefined && Number.isFinite(maxWidth)
      ? Math.max(1, maxWidth)
      : Infinity;
  const resolveRun = (run: DrawingTextRun): Required<DrawingTextRun> => ({
    text: run.text,
    fontSize: run.fontSize ?? element.fontSize,
    fontFamily: run.fontFamily ?? element.fontFamily,
    fontFace:
      run.fontFace ??
      (run.fontFamily !== undefined ? '' : (element.fontFace ?? '')),
    fontWeight: run.fontWeight ?? element.fontWeight ?? 400,
    fontStyle: run.fontStyle ?? element.fontStyle ?? 'normal',
    textDecoration: run.textDecoration ?? element.textDecoration ?? 'none',
    color: run.color ?? element.strokeColor,
  });
  const widthOf = (run: Required<DrawingTextRun>) =>
    getLineWidth(
      run.text,
      getFontString(run),
      element.letterSpacing,
      element.fontKerning
    );
  const makeLine = (
    pieces: Required<DrawingTextRun>[],
    start = 0,
    end = start
  ): TextLayoutLine => {
    const largest = pieces.reduce(
      (a, b) => (b.fontSize > a.fontSize ? b : a),
      resolveRun({ text: '' })
    );
    const height = largest.fontSize * element.lineHeight;
    let x = 0;
    const runs = pieces.map(piece => {
      const width = widthOf(piece);
      const run = { ...piece, x, width };
      x += width;
      return run;
    });
    return {
      start,
      end,
      text: pieces.map(piece => piece.text).join(''),
      width: x,
      height,
      baseline: getVerticalOffset(
        largest.fontFamily,
        largest.fontSize,
        height,
        largest.fontFace
      ),
      runs,
    };
  };

  if (!element.textRuns?.length) {
    const font = getFontString(element);
    const wrapped = getWrappedTextLines(
      element.originalText,
      font,
      boundedWidth,
      element.letterSpacing,
      element.fontKerning
    );
    const text = wrapped.map(line => line.text).join('\n');
    const lines = wrapped.map(line =>
      makeLine([resolveRun({ text: line.text })], line.start, line.end)
    );
    return {
      width: lines.reduce(
        (width, line) =>
          Math.max(
            width,
            line.text
              ? line.width
              : getLineWidth(
                  ' ',
                  font,
                  element.letterSpacing,
                  element.fontKerning
                )
          ),
        0
      ),
      height: lines.reduce((height, line) => height + line.height, 0),
      text,
      lines,
    };
  }

  const source = element.textRuns.map(resolveRun);
  const fullText = getTextRunContent(source);
  const indexed = source.map(run => ({ run, start: 0, end: 0 }));
  let offset = 0;
  for (const item of indexed) {
    item.start = offset;
    offset += item.run.text.length;
    item.end = offset;
  }
  // Locate only the runs touched by a source interval. Earlier runs are never
  // rescanned as a line grows (large documents commonly have thousands of runs).
  const piecesIn = (start: number, end: number) => {
    let low = 0;
    let high = indexed.length;
    while (low < high) {
      const mid = (low + high) >>> 1;
      if (indexed[mid].end <= start) low = mid + 1;
      else high = mid;
    }
    const pieces: { index: number; start: number; end: number }[] = [];
    for (
      let index = low;
      index < indexed.length && indexed[index].start < end;
      index++
    ) {
      const item = indexed[index];
      if (item.end > item.start)
        pieces.push({
          index,
          start: Math.max(start, item.start),
          end: Math.min(end, item.end),
        });
    }
    return pieces;
  };
  type Piece = ReturnType<typeof piecesIn>[number];
  const resolvePiece = (piece: Piece) => ({
    ...indexed[piece.index].run,
    text: fullText.slice(piece.start, piece.end),
  });
  const lines: TextLayoutLine[] = [];

  // No wrapping needs no trial prefixes: measure each source run once.
  if (boundedWidth === Infinity) {
    let start = 0;
    for (const text of fullText.split('\n')) {
      const end = start + text.length;
      lines.push(makeLine(piecesIn(start, end).map(resolvePiece), start, end));
      start = end + 1;
    }
  } else {
    let lineStart = 0;
    let lineEnd = 0;
    let lineWidth = 0;
    let linePieces: (Piece & { width: number })[] = [];
    const emit = (soft: boolean) => {
      let end = lineEnd;
      if (soft) {
        while (end > lineStart && /\s/u.test(fullText[end - 1])) end--;
      }
      lines.push(
        makeLine(
          linePieces
            .filter(piece => piece.start < end)
            .map(piece =>
              resolvePiece({ ...piece, end: Math.min(piece.end, end) })
            ),
          lineStart,
          end
        )
      );
      lineStart = lineEnd;
      lineWidth = 0;
      linePieces = [];
    };
    const propose = (end: number) => {
      const extra = piecesIn(lineEnd, end);
      const last = linePieces.at(-1);
      let width = lineWidth;
      let replacement: (Piece & { width: number }) | undefined;
      const append: (Piece & { width: number })[] = [];
      for (const piece of extra) {
        if (!replacement && !append.length && last?.index === piece.index) {
          // Retain native kerning inside the active run. Previously completed
          // runs contribute their already measured advance, without remeasurement.
          const joined = { ...last, end: piece.end };
          const advance = widthOf(resolvePiece(joined));
          replacement = { ...joined, width: advance };
          width += advance - last.width;
        } else {
          const advance = widthOf(resolvePiece(piece));
          append.push({ ...piece, width: advance });
          width += advance;
        }
      }
      return { width, replacement, append, end };
    };
    const accept = (proposal: ReturnType<typeof propose>) => {
      if (proposal.replacement)
        linePieces[linePieces.length - 1] = proposal.replacement;
      linePieces.push(...proposal.append);
      lineWidth = proposal.width;
      lineEnd = proposal.end;
    };
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: 'grapheme',
    });
    let position = 0;
    for (const hardLine of fullText.split('\n')) {
      const hardLineEnd = position + hardLine.length;
      const wholeLinePieces = piecesIn(position, hardLineEnd);
      let wholeLineWidth = 0;
      for (const piece of wholeLinePieces) {
        wholeLineWidth += widthOf(resolvePiece(piece));
        // Most bounded paragraphs need wrapping. Stop the preflight as soon
        // as that is known instead of measuring the rest of a large document.
        if (wholeLineWidth > boundedWidth) break;
      }
      if (wholeLineWidth <= boundedWidth) {
        lines.push(
          makeLine(wholeLinePieces.map(resolvePiece), position, hardLineEnd)
        );
        position = hardLineEnd + 1;
        lineStart = lineEnd = position;
        continue;
      }
      for (const token of parseTokens(hardLine)) {
        const end = position + token.length;
        let proposal = propose(end);
        if (!token.trim() || proposal.width <= boundedWidth) {
          accept(proposal);
        } else {
          if (lineEnd > lineStart) {
            emit(true);
            proposal = propose(end);
          }
          if (proposal.width <= boundedWidth) {
            accept(proposal);
          } else {
            for (const part of segmenter.segment(token)) {
              const partEnd = position + part.index + part.segment.length;
              let grapheme = propose(partEnd);
              if (grapheme.width > boundedWidth && lineEnd > lineStart) {
                emit(true);
                grapheme = propose(partEnd);
              }
              accept(grapheme);
            }
          }
        }
        position = end;
      }
      emit(false);
      position++;
      lineStart = lineEnd = position;
    }
  }

  return {
    text: lines.map(line => line.text).join('\n'),
    width: lines.reduce((width, line) => Math.max(width, line.width), 0),
    height: lines.reduce((sum, line) => sum + line.height, 0),
    lines,
  };
};

export const getTextListIndent = (
  element: LayoutText,
  paragraphCount: number
) =>
  !element.listStyle || element.listStyle === 'none'
    ? 0
    : Math.max(
        element.fontSize * 1.5,
        getLineWidth(
          `${paragraphCount}. `,
          getFontString(element),
          element.letterSpacing,
          element.fontKerning
        )
      );

/** Paragraph geometry augments the same inline measurer used by every renderer. */
export const getTextLayout = (element: LayoutText, maxWidth?: number) => {
  const padding = Math.max(0, element.textPadding ?? 0);
  const before = Math.max(0, element.paragraphSpacingBefore ?? 0);
  const after = Math.max(0, element.paragraphSpacingAfter ?? 0);
  const listed = element.listStyle && element.listStyle !== 'none';
  if (!padding && !before && !after && !listed)
    return getInlineTextLayout(element, maxWidth);
  const source = element.textRuns?.length
    ? getTextRunContent(element.textRuns)
    : element.originalText;
  const paragraphs = source.split('\n');
  const markerWidth = getTextListIndent(element, paragraphs.length);
  const lines: TextLayoutLine[] = [];
  let offset = 0;
  let runIndex = 0;
  let runStart = 0;
  for (const [index, paragraph] of paragraphs.entries()) {
    const end = offset + paragraph.length;
    const runs: DrawingTextRun[] = [];
    if (element.textRuns?.length) {
      while (runIndex < element.textRuns.length) {
        const run = element.textRuns[runIndex];
        const runEnd = runStart + run.text.length;
        if (runStart >= end) break;
        if (runEnd > offset)
          runs.push({
            ...run,
            text: run.text.slice(
              Math.max(0, offset - runStart),
              Math.min(run.text.length, end - runStart)
            ),
          });
        if (runEnd > end) break;
        runStart = runEnd;
        runIndex++;
      }
    }
    const part = getInlineTextLayout(
      {
        ...element,
        originalText: paragraph,
        textRuns: runs.length ? runs : undefined,
      },
      maxWidth === undefined
        ? undefined
        : Math.max(1, maxWidth - padding * 2 - markerWidth)
    );
    for (const [lineIndex, line] of part.lines.entries()) {
      const first = lineIndex === 0;
      const last = lineIndex === part.lines.length - 1;
      const top = (first ? before : 0) + (index === 0 && first ? padding : 0);
      const bottom =
        (last ? after : 0) +
        (index === paragraphs.length - 1 && last ? padding : 0);
      lines.push({
        ...line,
        start: line.start + offset,
        end: line.end + offset,
        width: line.width + padding * 2 + markerWidth,
        height: line.height + top + bottom,
        baseline: line.baseline + top,
        runs: line.runs.map(run => ({
          ...run,
          x: run.x + padding + markerWidth,
        })),
        marker:
          listed && first
            ? {
                ...part.lines[0].runs[0],
                text: element.listStyle === 'number' ? `${index + 1}.` : '•',
                fontSize: element.fontSize,
                fontFamily: element.fontFamily,
                fontFace: element.fontFace ?? '',
                fontWeight: element.fontWeight ?? 400,
                fontStyle: element.fontStyle ?? 'normal',
                textDecoration: 'none',
                color: element.strokeColor,
                x: padding,
                width: markerWidth,
              }
            : undefined,
      });
    }
    offset = end + 1;
  }
  return {
    text: lines.map(line => line.text).join('\n'),
    lines,
    width: lines.reduce((width, line) => Math.max(width, line.width), 0),
    height: lines.reduce((sum, line) => sum + line.height, 0),
  };
};

export const getTextVerticalOffset = (
  element: Pick<
    ExcalidrawTextElement,
    'height' | 'verticalAlign' | 'textOverflow'
  >,
  contentHeight: number
) => {
  if (element.textOverflow !== 'clip') return 0;
  const spare = Math.max(0, element.height - contentHeight);
  return element.verticalAlign === 'middle'
    ? spare / 2
    : element.verticalAlign === 'bottom'
      ? spare
      : 0;
};

/** Resize absolute per-run sizes along with the containing text object. */
export const scaleTextRuns = (
  runs: DrawingTextRun[] | undefined,
  scale: number
) => {
  if (!runs || scale === 1) return runs;
  return runs.map(run =>
    run.fontSize === undefined
      ? run
      : { ...run, fontSize: run.fontSize * scale }
  );
};

/** Whole-object style edits remove matching local overrides without changing content. */
export const clearTextRunStyles = (
  runs: DrawingTextRun[] | undefined,
  keys: readonly Exclude<keyof DrawingTextRun, 'text'>[]
) => {
  if (!runs || !runs.some(run => keys.some(key => key in run))) return runs;
  return runs.map(run => {
    const next = { ...run };
    for (const key of keys) delete next[key];
    return next;
  });
};

/** Absolute paragraph insets scale with text during a proportional resize. */
export const scaleTextParagraphSpacing = (
  element: ExcalidrawTextElement,
  scale: number
) => ({
  textPadding:
    element.textPadding === undefined ? undefined : element.textPadding * scale,
  paragraphSpacingBefore:
    element.paragraphSpacingBefore === undefined
      ? undefined
      : element.paragraphSpacingBefore * scale,
  paragraphSpacingAfter:
    element.paragraphSpacingAfter === undefined
      ? undefined
      : element.paragraphSpacingAfter * scale,
});
